// Synchronous, no-network read of the session the better-auth cross-domain
// client persists in the browser. The client (see @convex-dev/better-auth
// cross-domain plugin) stores two localStorage entries under the default
// "better-auth" prefix:
//
//   better-auth_cookie        JSON map of cookies, including <prefix>.session_token
//   better-auth_session_data  cached /get-session payload (the user + session)
//
// Reading these lets the app know, on the very first render and before any
// round-trip, whether a session token is present. That powers the "immediate"
// auth detection the routing layer needs: with no stored token we can show the
// login form (or redirect a protected page) instantly, and with a stored token
// we show a loading state instead of flashing the wrong screen while the
// backend confirms the session. The backend remains the source of truth — this
// is only an optimistic, fail-safe hint for first paint.

const STORAGE_PREFIX = 'better-auth'
const COOKIE_KEY = `${STORAGE_PREFIX}_cookie`
const SESSION_DATA_KEY = `${STORAGE_PREFIX}_session_data`

type StoredCookieEntry = {
  value?: string
  expires?: string | null
}

function readLocalStorage(key: string): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private mode / storage disabled — treat as no stored session.
    return null
  }
}

function hasUnexpiredSessionTokenCookie(rawCookie: string | null): boolean {
  if (!rawCookie) {
    return false
  }

  let parsed: Record<string, StoredCookieEntry>

  try {
    parsed = JSON.parse(rawCookie) as Record<string, StoredCookieEntry>
  } catch {
    return false
  }

  return Object.entries(parsed).some(([key, entry]) => {
    if (!key.includes('session_token') || !entry?.value) {
      return false
    }

    // Respect a locally-known expiry so an expired token reads as "signed out"
    // without waiting on the network.
    if (entry.expires) {
      const expiresAt = new Date(entry.expires).getTime()
      if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
        return false
      }
    }

    return true
  })
}

function hasCachedSessionData(rawSessionData: string | null): boolean {
  if (!rawSessionData) {
    return false
  }

  try {
    const parsed = JSON.parse(rawSessionData) as unknown
    if (!parsed || typeof parsed !== 'object') {
      return false
    }

    // Signed-out state is persisted as "{}"; a real session carries a `session`.
    return 'session' in (parsed as Record<string, unknown>)
  } catch {
    return false
  }
}

/**
 * Returns true when the browser holds an un-expired session token. Safe on the
 * server (returns false) and never throws.
 */
export function hasStoredSession(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  return (
    hasUnexpiredSessionTokenCookie(readLocalStorage(COOKIE_KEY)) ||
    hasCachedSessionData(readLocalStorage(SESSION_DATA_KEY))
  )
}

/**
 * Clears the locally cached session, mirroring what the cross-domain client
 * writes on sign-out. Used to clean up a stale token once the backend has
 * rejected it, so the next first-paint check reads "signed out" instantly.
 */
export function clearStoredSession() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.localStorage.setItem(COOKIE_KEY, '{}')
    window.localStorage.setItem(SESSION_DATA_KEY, '{}')
  } catch {
    // Ignore — nothing we can do if storage is unavailable.
  }
}
